"use client";

import React, { useEffect } from "react";

import { strings } from "@perceptron/constants";
import { AnnotationSource } from "@perceptron/core/types";
import { PerceptronMarkLoop } from "@perceptron/ui/components/animations/perceptron-mark-loop";
import { Icon } from "@perceptron/ui/components/icon";
import { cn } from "@perceptron/utils/cn";
import Image from "next/image";
import remarkGfm from "remark-gfm";

import { StreamSourceProvider } from "@demo/contexts/StreamSourceContext";
import { FeedbackButtons } from "@demo/features/report";
import { useAutoScroll } from "@demo/hooks/useAutoScroll";
import { usePacedText } from "@demo/hooks/usePacedText";
import { AgentState, useModelStore } from "@demo/store/model";
import { useToolCallStore } from "@demo/store/tool-calls";
import { normalizeTrackLikeCollections } from "@demo/utils/normalizeTrackLikeCollections";

import { ANNOTATION_TAG_PATTERN } from "./annotation-tags";
import { CopyResponseButton } from "./CopyResponseButton";
import { composeFeedbackContext } from "./feedbackContext";
import { FinishReasonBanner } from "./FinishReasonBanner";
import LoadingDots from "./LoadingDots";
import { MarkdownBlocks } from "./MarkdownBlocks";
import { createMarkdownComponents } from "./MarkdownComponents";
import ReasoningSection from "./ReasoningSection";
import {
  codeTickTransform,
  remarkPointBoxToKebab,
  remarkTrackAlias,
  remarkUnwrapAnnotationCode,
} from "./remark-plugins";
import { SourcesRow } from "./SourcesRow";
import { describeToolActivity, runningToolCall } from "./tool-calls/toolActivity";
import { ToolCallList } from "./tool-calls/ToolCallList";
import { toolCallAnchor, toolCallSlotMarkup } from "./tool-calls/ToolCallSlot";

/** The remark plugins both the thinking and the answer render with. */
export const remarkPlugins = [
  remarkUnwrapAnnotationCode,
  codeTickTransform,
  remarkPointBoxToKebab,
  remarkTrackAlias,
  remarkGfm,
];

const markdownComponents = createMarkdownComponents();

const JSON_PREFIX_PATTERN = /^[{[]/;
const JSON_SUFFIX_PATTERN = /[}\]]$/;

/**
 * Pre-processes the raw response for the markdown renderer. Notably,
 * collapses whitespace between adjacent tags onto one line — otherwise inline
 * HTML in a paragraph splits into separate mdast nodes and rehype-raw parses
 * each independently, flattening nesting (collection > track would render
 * as siblings instead).
 */
const cleanTextForMarkdown = (text: string | null | undefined): string => {
  if (!text) return "";

  // Must run before any other rewrite so it matches what parseAnnotations sees
  // — parser/walker counter symmetry breaks if the two sides disagree on shape.
  let cleaned = normalizeTrackLikeCollections(text);

  // Self-closing clip → paired form (markdown doesn't support self-closing).
  cleaned = cleaned.replace(/<clip([^>]*)\/>/g, "<clip$1></clip>");

  // `<point_box>` has an underscore (invalid HTML tag-name char), so
  // remark-parse classifies it as text and decodes any entity references in
  // its attributes. That turns `mention="&quot;A&quot;"` into the malformed
  // `mention=""A""`, which rehype-raw then mis-parses (`mention` becomes
  // empty, the rest leaks into stray attrs and the label disappears). Other
  // custom tags (`point`, `polygon`, `clip`, `collection`, `track`) have
  // valid HTML tag names so remark keeps them as raw HTML and entities
  // survive untouched. Re-escape `&` to `&amp;` only inside `<point_box>`
  // attribute values so the entity survives remark's text decoding and
  // reaches rehype-raw's HTML attribute parser intact.
  cleaned = cleaned.replace(/<point_box\b[^>]*>/g, (tag) =>
    tag.replace(/="([^"]*)"/g, (_, value: string) => `="${value.replace(/&/g, "&amp;")}"`),
  );

  // Collapse `<…>\n<` so adjacent XML tags stay on one line — needed for both
  // sibling tags (`</track>\n<track>`) and parent→child nesting (`<collection
  // mention="…">\n  <track …>`); without it rehype-raw parses each as a
  // separate inline-HTML node and the nesting is lost.
  // The capture is `<` + non-`>`/non-newline + `>` so we only match real
  // single-line tag-like spans (a bare `>` in prose stays untouched). The
  // trailing `<` is matched as a lookahead so the regex engine doesn't consume
  // it — otherwise scanning advances past the next opening tag and we'd miss
  // chained transitions like `<collection>\n<track>\n<point_box>`.
  cleaned = cleaned.replace(/(<[^>\n]+>)\s*\n\s*(?=<)/g, "$1 ");

  cleaned = cleaned.replace(/\n+$/, "");

  // Wrap top-level JSON containing annotation tags in `<pre><code>` so the
  // styled code component fires. The trailing-bracket check also gates out
  // streaming partials — wrapping mid-stream would feed an unclosed `<point`
  // to the HTML parser.
  const trimmed = cleaned.trim();
  if (JSON_PREFIX_PATTERN.test(trimmed) && JSON_SUFFIX_PATTERN.test(trimmed) && ANNOTATION_TAG_PATTERN.test(trimmed)) {
    cleaned = `<pre><code>${cleaned}</code></pre>`;
  }

  return cleaned;
};

/**
 * Reasoning text with a tool-call slot at every offset where a call began, so each call renders
 * where it happened and the thinking that followed continues below it. Display only: annotations
 * are parsed from the raw text.
 *
 * A slot is named by the call's own anchor, which is how the slot finds its calls, but placed at that
 * anchor clamped to `receivedLength`, the reasoning received so far: a call that began while its
 * reasoning was still buffered (or whose buffered reasoning a Stop discarded) sits at the end rather
 * than vanishing. `reasoningText` is what is on screen, which the paced reveal can hold behind what was
 * received; a slot past it waits until the reveal reaches it.
 */
export const withToolCallSlots = (
  reasoningText: string,
  anchors: readonly number[],
  hasToolCalls: boolean,
  receivedLength: number = reasoningText.length,
): string => {
  // Nothing to anchor in: without reasoning the cards render in the flat list instead.
  if (!hasToolCalls || reasoningText === "") return reasoningText;
  const slots = [...new Set(anchors)]
    .map((anchor) => ({ anchor, at: Math.max(0, Math.min(anchor, receivedLength)) }))
    .filter(({ at }) => at <= reasoningText.length)
    .sort((a, b) => a.at - b.at || a.anchor - b.anchor);
  let result = "";
  let start = 0;
  for (const { anchor, at } of slots) {
    result += reasoningText.slice(start, at) + toolCallSlotMarkup(anchor);
    start = at;
  }
  return result + reasoningText.slice(start);
};

/** Lengthens the auto-scroll key when the action row appears, so a reader following the stream is taken to it. */
const ANSWER_ACTIONS_SCROLL_MARK = "\u0000actions";

const AgentOutput: React.FC<{ className?: string; initialContent?: string }> = ({
  className = "",
  initialContent = "",
}) => {
  const agentState = useModelStore((state) => state.agentState);
  const agentText = useModelStore((state) => state.agentText);
  const reasoningText = useModelStore((state) => state.reasoningText);
  const isReasoning = useModelStore((state) => state.isReasoning);
  const finishReason = useModelStore((state) => state.finishReason);
  const isEmptyResponse = useModelStore((state) => state.isEmptyResponse);
  const addAnnotations = useModelStore((state) => state.addAnnotations);
  const hasToolCalls = useToolCallStore((state) => state.order.length > 0);
  // The call running now, if any: it keeps the thinking open (even once the thinking it is anchored in has
  // finished) and names what it is doing in the thinking label.
  const runningCall = useToolCallStore((state) => runningToolCall(state.order, state.calls));
  const hasRunningToolCall = runningCall !== undefined;
  const toolCalls = useToolCallStore((state) => state.calls);
  // Where each call began in the reasoning; the same offset for calls announced together.
  const toolCallAnchors = React.useMemo(() => Object.values(toolCalls).map(toolCallAnchor), [toolCalls]);

  const isStreamingRun = !initialContent && agentState === AgentState.STREAMING;
  // Revealed at an even pace: the model hands over whole paragraphs a frame, too fast to read as writing.
  const rawContent = !!initialContent ? initialContent : agentText;
  const pacedReasoning = usePacedText(reasoningText, isStreamingRun);
  // The answer waits for the thinking to finish showing, so one stream reveals at a time within the cap.
  // Thinking counts as finished only once its reveal has caught up, so it never folds away mid-sentence.
  const isThinkingRevealing = pacedReasoning.text.length < reasoningText.length;
  // While the finished thinking folds away, the answer waits: text arriving below a collapsing section, with
  // the view scrolling to follow it, fights the fold. What arrives meanwhile is held and revealed after.
  const [isThoughtFolding, setIsThoughtFolding] = React.useState(false);
  const pacedContent = usePacedText(rawContent, isStreamingRun, isThinkingRevealing || isThoughtFolding);
  const content = pacedContent.text;

  const cleanContent = React.useMemo(() => cleanTextForMarkdown(content), [content]);

  const cleanReasoningText = React.useMemo(
    () =>
      cleanTextForMarkdown(withToolCallSlots(pacedReasoning.text, toolCallAnchors, hasToolCalls, reasoningText.length)),
    [pacedReasoning.text, toolCallAnchors, hasToolCalls, reasoningText.length],
  );

  // The answer has fully arrived and been revealed: the row under it (sources, copy, feedback) shows.
  const isAnswerComplete =
    agentState !== AgentState.STREAMING &&
    !pacedContent.isRevealing &&
    !pacedReasoning.isRevealing &&
    cleanContent !== "";
  // Follows the thinking and the answer as they stream, and the action row when it arrives after them:
  // it is not text, but it is more to scroll to, and would otherwise land below the view.
  const {
    scrollContainerRef,
    handleScroll,
    handleTouchMove,
    handleWheel,
    resetUserScrolling,
    scrollToLatest,
    isUserScrolling,
  } = useAutoScroll(cleanReasoningText + cleanContent + (isAnswerComplete ? ANSWER_ACTIONS_SCROLL_MARK : ""));

  const isEmpty = cleanContent === "" && reasoningText === "" && !hasToolCalls;
  const isEvaluating = agentState === AgentState.EVALUATING;
  const hasFinished = agentState !== AgentState.STREAMING;
  const isRunWaiting = isEvaluating || agentState === AgentState.STREAMING;
  // Words keep their entrance until the paced reveal has caught up and the last of them has settled.
  const isFadingIn = !hasFinished || pacedContent.isRevealing || pacedReasoning.isRevealing;
  // Words streamed in keep their fade spans after the run, until the next run clears the output. Taking them
  // out re-renders the whole answer at once, and Safari, which has no scroll anchoring, resets the pane's
  // scroll to the top when it does. Finished spans carry no animation (`backwards` fill), so they cost little.
  const [keepWordSpans, setKeepWordSpans] = React.useState(isFadingIn);
  if (isFadingIn && !keepWordSpans) setKeepWordSpans(true);
  if (!isFadingIn && keepWordSpans && rawContent === "" && reasoningText === "") setKeepWordSpans(false);
  const showJumpToLatest = isUserScrolling && isFadingIn;
  // Feedback is for a finished run.
  const hasFeedbackButtons = agentState === AgentState.IDLE && !!agentText;

  const showEmptyResponseNotice = isEmpty && !isEvaluating && isEmptyResponse;
  const showFinishReasonBanner =
    agentState === AgentState.IDLE && finishReason && ["length", "tool_error", "tool_limit"].includes(finishReason);

  // Parse reasoning text annotations
  useEffect(() => {
    addAnnotations(reasoningText, AnnotationSource.REASONING);
  }, [reasoningText, addAnnotations]);

  // Parse content text annotations
  useEffect(() => {
    addAnnotations(rawContent, AnnotationSource.CONTENT);
  }, [rawContent, addAnnotations]);

  // Reset user scrolling state when streaming starts
  useEffect(() => {
    if (agentState === AgentState.EVALUATING) {
      resetUserScrolling();
    }
  }, [agentState, resetUserScrolling]);

  return (
    <div className="relative h-full w-full">
      <div
        className={cn(
          "dark h-full w-full max-w-full overflow-y-auto overflow-x-hidden overscroll-contain break-words rounded-2xl bg-black p-4 text-white [scrollbar-gutter:stable] [&::-webkit-scrollbar-thumb]:bg-gray-600 [&::-webkit-scrollbar-track]:bg-black [&::-webkit-scrollbar]:w-2",
          // A size container, so descendants (the reverse-image-search preview) can size against the box with `cqh`.
          "[container-type:size]",
          className,
        )}
        data-testid="markdown-renderer-container"
        ref={scrollContainerRef}
        onScroll={handleScroll}
        onTouchMove={handleTouchMove}
        onWheel={handleWheel}
      >
        {isEmpty || isEvaluating ? (
          <div className="relative flex h-full flex-1 flex-col items-center justify-center">
            {!showEmptyResponseNotice && (
              <div className="relative mb-4 flex h-20 w-24 items-center justify-center">
                {/* Waiting on the run: the mark loops. Otherwise the still logo. */}
                {isRunWaiting ? (
                  <PerceptronMarkLoop
                    aria-label={strings.agentOutput.logo}
                    className="w-24 text-white"
                    data-testid="waiting-mark"
                    playing
                    role="img"
                  />
                ) : (
                  <Image
                    alt={strings.agentOutput.logo}
                    className="h-20 w-24"
                    height={80}
                    priority
                    src="/demo/assets/logo-light.svg"
                    width={96}
                  />
                )}
              </div>
            )}
            {showEmptyResponseNotice ? (
              <div className="flex flex-col items-center gap-2 px-6 text-center" data-testid="empty-response-notice">
                <div className="font-ppmondwest title-2">{strings.agentOutput.emptyResponseTitle}</div>
                <div className="max-w-sm text-muted-foreground label-2">
                  {strings.agentOutput.emptyResponseDescription}
                </div>
              </div>
            ) : (
              <div className="font-ppmondwest text-[24px] text-white">
                <LoadingDots agentState={agentState} />
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Render reasoning section if present */}
            {cleanReasoningText && (
              <ReasoningSection
                activity={runningCall ? describeToolActivity(runningCall) : undefined}
                holdOpen={hasRunningToolCall}
                isStreaming={isReasoning || isThinkingRevealing}
                onFoldingChange={setIsThoughtFolding}
              >
                <StreamSourceProvider source={AnnotationSource.REASONING}>
                  <MarkdownBlocks
                    components={markdownComponents}
                    hasFinished={hasFinished}
                    isFadingIn={keepWordSpans}
                    remarkPlugins={remarkPlugins}
                    text={cleanReasoningText}
                  />
                </StreamSourceProvider>
              </ReasoningSection>
            )}

            {/* Without reasoning there is no thought stream to anchor the cards in. */}
            {reasoningText === "" && <ToolCallList />}

            <StreamSourceProvider source={AnnotationSource.CONTENT}>
              <MarkdownBlocks
                components={markdownComponents}
                hasFinished={hasFinished}
                isFadingIn={keepWordSpans}
                remarkPlugins={remarkPlugins}
                text={cleanContent}
              />
            </StreamSourceProvider>

            {/* Under the finished answer: its sources on the left (when it searched), copy and feedback on the
                right. In the flow rather than floating over the corner, so nothing covers the last line. */}
            {isAnswerComplete && (
              <div className="tool-call-enter mt-4 flex items-start gap-2" data-testid="answer-actions">
                {hasToolCalls && <SourcesRow />}
                <div className="dark ml-auto flex shrink-0 items-center gap-1">
                  <CopyResponseButton text={rawContent} />
                  <FeedbackButtons
                    getContext={composeFeedbackContext}
                    resultKey={hasFeedbackButtons ? agentText : null}
                  />
                </div>
              </div>
            )}

            {showFinishReasonBanner && <FinishReasonBanner finishReason={finishReason} />}
          </>
        )}
      </div>
      {/* Scrolled up while the answer is still arriving: a way back down that also resumes following. A small
          round button in the corner. */}
      <button
        aria-hidden={!showJumpToLatest || undefined}
        aria-label={strings.agentOutput.jumpToLatest}
        className={cn(
          "dark absolute right-3 z-10 flex size-8 items-center justify-center rounded-full border border-white/20 bg-neutral-800 text-white shadow-[0_4px_12px_rgba(0,0,0,0.6)] hover:border-white/30 hover:bg-neutral-700",
          "bottom-3 transition-[opacity,transform,border-color,background-color] duration-200 ease-out motion-reduce:transition-none",
          showJumpToLatest ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-2 opacity-0",
        )}
        data-testid="jump-to-latest"
        tabIndex={showJumpToLatest ? 0 : -1}
        title={strings.agentOutput.jumpToLatest}
        type="button"
        onClick={scrollToLatest}
      >
        <Icon className="size-4" name="chevron-down" />
      </button>
    </div>
  );
};

export default AgentOutput;
